import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import type { MapStyle } from '../MapControlsFAB';

import {
  MAPBOX_TOKEN,
  MAP_STYLES,
  MAP_MAX_ZOOM,
  MAP_DEFAULT_ZOOM,
  transformStyleUrls,
} from '../config/mapConfig';
import { clearMapboxTileCacheIfPromoted, fetchPromotedTileVersions } from '../../../lib/tileVersions';
import {
  prepareMapForLayerToggles,
} from '../../../utils/mapLayerPromotion';
import { enableMapbox3dModels } from './useMapLayers';

const IS_PLAYWRIGHT_TEST = import.meta.env.VITE_PLAYWRIGHT_TEST === 'true';
/** Minimal style when JSON fetch fails in CI — still creates a canvas for E2E. */
const PLAYWRIGHT_MINIMAL_STYLE = {
  version: 8,
  name: 'Playwright Test',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#e8e4dc' },
    },
  ],
};
/** If `load` never fires (network/style/tile issues), avoid an infinite splash. */
const MAP_LOAD_FALLBACK_MS = IS_PLAYWRIGHT_TEST ? 2_000 : 22_000;
/** Style JSON fetch timeout — without a style the map never inits and splash never clears. */
const STYLE_FETCH_TIMEOUT_MS = IS_PLAYWRIGHT_TEST ? 10_000 : 30_000;

interface UseMapInitializationProps {
  mapStyle: MapStyle;
  isTerrainEnabled: boolean;
}

export function useMapInitialization({
  mapStyle,
  isTerrainEnabled,
}: UseMapInitializationProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const { flyRequestId, setMapLoaded } = useMapStore();

  const [ghanaStyleJson, setGhanaStyleJson] = useState<any>(null);
  const mapInstanceIdRef = useRef(0);
  const styleFetchRef = useRef<AbortController | null>(null);

  // Fetch style JSON on initial mount
  useEffect(() => {
    if (ghanaStyleJson) return;

    const styleUrl = MAP_STYLES[mapStyle];
    let cancelled = false;

    const finishStyleLoad = (style: any) => {
      if (cancelled) return;
      setGhanaStyleJson(style);
    };

    const failStyleLoad = (message: string, err?: unknown) => {
      if (cancelled) return;
      if (err) console.error(`Failed to load ${mapStyle} style:`, err);
      else console.error(message);
    };

    // E2E: skip network style fetch — large JSON + tile URL transforms are slow/flaky in CI preview.
    if (IS_PLAYWRIGHT_TEST) {
      finishStyleLoad(PLAYWRIGHT_MINIMAL_STYLE);
      return () => { cancelled = true; };
    }

    if (styleUrl.startsWith('mapbox://')) {
      finishStyleLoad(styleUrl);
      return () => { cancelled = true; };
    }
    styleFetchRef.current?.abort();
    const controller = new AbortController();
    styleFetchRef.current = controller;
    const fetchTimeout = window.setTimeout(() => controller.abort(), STYLE_FETCH_TIMEOUT_MS);

    fetch(`${styleUrl}?t=${Date.now()}`, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`Style fetch HTTP ${res.status}`);
        return res.json();
      })
      .then(async style => {
        const { layers: layerVersions, cacheBust } = await fetchPromotedTileVersions(controller.signal);
        await clearMapboxTileCacheIfPromoted(cacheBust, layerVersions);
        const transformed = transformStyleUrls(style, layerVersions);
        finishStyleLoad(transformed);
      })
      .catch(err => {
        if (cancelled || controller.signal.aborted) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (IS_PLAYWRIGHT_TEST) {
          finishStyleLoad(PLAYWRIGHT_MINIMAL_STYLE);
          return;
        }
        failStyleLoad(`Style load failed: ${msg}`, err);
      })
      .finally(() => {
        window.clearTimeout(fetchTimeout);
        if (styleFetchRef.current === controller) {
          styleFetchRef.current = null;
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(fetchTimeout);
      if (styleFetchRef.current === controller) {
        styleFetchRef.current = null;
      }
    };
  }, [mapStyle, ghanaStyleJson, setMapLoaded]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (!ghanaStyleJson) return;

    const instanceId = ++mapInstanceIdRef.current;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Initial view: Ghana overview until geolocation resolves; hash overrides for share links.
    let initialCenter: [number, number] = [-1.0232, 7.9465];
    let initialZoom = MAP_DEFAULT_ZOOM;
    const params = new URLSearchParams(window.location.search);
    const isHeroMode = params.get('mode') === 'hero';
    const hash = window.location.hash;
    if (hash && (isHeroMode || !params.get('mode'))) {
      const match = hash.match(/#(\d+(?:\.\d+)?)\/([-\d.]+)\/([-\d.]+)/);
      if (match) {
        initialZoom = parseFloat(match[1]);
        initialCenter = [parseFloat(match[3]), parseFloat(match[2])];
      }
    }

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: ghanaStyleJson,
      center: initialCenter,
      zoom: initialZoom,
      pitch: 45,
      bearing: 0,
      projection: 'globe' as any,
      attributionControl: false,
      touchPitch: true,
      minZoom: 2,
      maxZoom: MAP_MAX_ZOOM,
      maxPitch: 85,
      renderWorldCopies: false,
      fadeDuration: 0,
      // @ts-ignore - indoor option exists but not in types
      indoor: false,
    });

    let loadFallbackTimer: number | undefined = window.setTimeout(() => {
      loadFallbackTimer = undefined;
      if (!mapRef.current || mapInstanceIdRef.current !== instanceId) return;
      markMapReady('fallback-timeout');
    }, MAP_LOAD_FALLBACK_MS) as unknown as number;

    const clearLoadFallback = () => {
      if (loadFallbackTimer !== undefined) {
        window.clearTimeout(loadFallbackTimer);
        loadFallbackTimer = undefined;
      }
    };

    const configureMapAfterReady = () => {
      const map = mapRef.current;
      if (!map || mapInstanceIdRef.current !== instanceId) return;

      try {
        prepareMapForLayerToggles(map);

        if (isTerrainEnabled && map.getSource('ghana-terrain-rgb')) {
          map.setTerrain({ source: 'ghana-terrain-rgb', exaggeration: 1.5 });
        }

        try {
          enableMapbox3dModels(map);
        } catch {
          // Config property may not be available
        }

        // Geolocation is handled once in App.tsx (permission-aware). Avoid duplicate
        // getCurrentPosition calls here — they race and can set conflicting coords.
      } catch (err) {
        console.error('Post-load map configure failed:', err);
      }
    };

    const markMapReady = (reason: string, configure = false) => {
      if (!mapRef.current || mapInstanceIdRef.current !== instanceId) return;
      if (useMapStore.getState().isMapLoaded && reason !== 'load') return;
      clearLoadFallback();
      setMapLoaded(true);
      if (configure) {
        configureMapAfterReady();
      }
    };

    mapRef.current.on('load', () => {
      markMapReady('load', true);
    });

    // Re-apply settings on style changes (e.g. when switching styles)
    mapRef.current.on('style.load', () => {
      if (!mapRef.current || mapInstanceIdRef.current !== instanceId) return;
      // Initial style.load fires before `load` — wait until the map is ready.
      if (!useMapStore.getState().isMapLoaded) return;
      try {
        prepareMapForLayerToggles(mapRef.current);
      } catch (err) {
        console.error('style.load configure failed:', err);
      }

      if (isTerrainEnabled && mapRef.current.getSource('ghana-terrain-rgb')) {
        mapRef.current.setTerrain({ source: 'ghana-terrain-rgb', exaggeration: 1.5 });
      }

      try {
        enableMapbox3dModels(mapRef.current);
      } catch {
        // Config property may not be available
      }
    });

    return () => {
      clearLoadFallback();
      mapRef.current?.remove();
      mapRef.current = null;
      if (mapInstanceIdRef.current === instanceId) {
        setMapLoaded(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghanaStyleJson]);

  // Handle style switching after initialization
  const previousMapStyle = useRef<MapStyle>(mapStyle);
  useEffect(() => {
    if (!mapRef.current || previousMapStyle.current === mapStyle) {
      previousMapStyle.current = mapStyle;
      return;
    }

    const styleUrl = MAP_STYLES[mapStyle];
    const isMapboxUrl = styleUrl.startsWith('mapbox://');

    if (isMapboxUrl) {
      (mapRef.current as any)?.setStyle(styleUrl, { diff: false });
    } else {
      fetch(`${styleUrl}?t=${Date.now()}`)
        .then(res => res.json())
        .then(async styleJson => {
          const { layers: layerVersions, cacheBust } = await fetchPromotedTileVersions();
          await clearMapboxTileCacheIfPromoted(cacheBust, layerVersions);
          const transformed = transformStyleUrls(styleJson, layerVersions);
          (mapRef.current as any)?.setStyle(transformed, { diff: false });
        })
        .catch(err => console.error(`Failed to load ${mapStyle} style:`, err));
    }

    previousMapStyle.current = mapStyle;
  }, [mapStyle]);

  // Fly only on explicit flyToLocation requests — not when the user pans/zooms.
  useEffect(() => {
    if (flyRequestId === 0 || !mapRef.current) return;
    const { center: target, zoom: targetZoom } = useMapStore.getState();
    if (!target) return;
    mapRef.current.flyTo({
      center: [target.lng, target.lat],
      zoom: targetZoom,
      duration: 1200,
      essential: false,
    });
  }, [flyRequestId]);

  // Expose setStyle for imperative use
  const setStyle = (style: MapStyle) => {
    if (!mapRef.current) return;
    const styleUrl = MAP_STYLES[style];
    const isMapboxUrl = styleUrl.startsWith('mapbox://');

    if (isMapboxUrl) {
      (mapRef.current as any)?.setStyle(styleUrl, { diff: false });
    } else {
      fetch(`${styleUrl}?t=${Date.now()}`)
        .then(res => res.json())
        .then(async styleJson => {
          const { layers: layerVersions, cacheBust } = await fetchPromotedTileVersions();
          await clearMapboxTileCacheIfPromoted(cacheBust, layerVersions);
          const transformed = transformStyleUrls(styleJson, layerVersions);
          (mapRef.current as any)?.setStyle(transformed, { diff: false });
        })
        .catch(err => console.error(`Failed to load ${style} style:`, err));
    }
  };

  return { mapRef, mapContainerRef, setStyle };
}
